import { createContext, useCallback, useEffect, useState } from 'react';
import { IAuthContext, IAuthProps } from './authentication.interface';
import { clearCookies, getCookies, setCookie } from '../../helpers/utils';
import { IAuthTokens } from '../../interfaces/authentication.interface';
import { IUser } from '../../interfaces/user.interface';
import { COOKIES } from '../../constants';
import { useGetUser } from '../../hooks/useUser';

const authenticationInitValues = {
    isAuthenticated: undefined,
    setIsAuthenticated: Function.prototype(),
    authTokens: undefined,
    setAuthTokens: Function.prototype(),
    user: {
        id: "string",
        email: "string",
        name: "string",
        active: true,
        deleted: false,
        showOnboarding: true,
        company: undefined,
        initialSurveyAnswerId: "string",
        jobTitle: "string",
        nickname: "string",
        selfQualifyingAnswerId: "string"
    },
    setUser: Function.prototype()
};

export const AuthenticationContext = createContext<IAuthContext>(authenticationInitValues as any);

const AuthenticationProvider = ({ children }: IAuthProps) => {
    const [user, setUser] = useState<IUser>();
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>();
    const [authTokens, setAuthTokens] = useState<IAuthTokens>();
    const getUserDetails = useGetUser();

    const getUser = useCallback(async (): Promise<void> => {
        const response = await getUserDetails.mutateAsync();
        if (response.success) {
            setUser(response.data as IUser);
            setIsAuthenticated(true);
        } else {
            setIsAuthenticated(false);
            clearCookies();
        }
    }, [getUserDetails]);

    useEffect(() => {
        const cookies = getCookies();

        if (cookies[COOKIES.REFRESH_TOKEN]) {
            setAuthTokens({
                access_token: cookies[COOKIES.TOKEN] as string,
                refresh_token: cookies[COOKIES.REFRESH_TOKEN] as string
            });
        } else {
            setIsAuthenticated(false);
        }
    }, []);

    useEffect(() => {
        const cookies = getCookies();
        if (authTokens) {
            setCookie(
                COOKIES.TOKEN,
                authTokens?.access_token,
                null,
                '/',
                null,
                false
            );
            setCookie(
                COOKIES.REFRESH_TOKEN,
                authTokens?.refresh_token,
                null,
                '/',
                null,
                false
            );
            
            getUser();
        } else if (!cookies[COOKIES.TOKEN] && !cookies[COOKIES.REFRESH_TOKEN]) {
            setIsAuthenticated(false);
        }
    }, [authTokens]);

    return (
        <AuthenticationContext.Provider
            value={{
                isAuthenticated,
                setIsAuthenticated,
                authTokens,
                setAuthTokens,
                user,
                setUser
            }}>
            {children}
        </AuthenticationContext.Provider>
    );
};

export default AuthenticationProvider;
